import React, { useEffect, useMemo, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { ModelInterface } from '@interfaces/models/deposit';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import VcImage from '@components/atoms/vc-images/vc-image';
import Icon from '@components/atoms/icon/icon';
import { ListingContext } from '@context/listing.context';
import useWindowSize from '@hooks/use-window-size';
import ModelVariations from './model-variations';
import styles from './index.module.scss';

interface ModelsModalPropsInterface {
  setIsModelsModalOpen: (open: boolean) => void;
  modalDialogTitleId: string;
  suggested?: ModelInterface[];
}

const ModelsModal: React.FC<ModelsModalPropsInterface> = ({
  setIsModelsModalOpen,
  modalDialogTitleId,
  suggested,
}): React.JSX.Element => {
  const { t } = useTranslation('pricing-calculator');
  const { isWiderThanMd } = useWindowSize();
  const models = useContextSelector(ListingContext, (state) => state.models);
  const listingDraftPayload = useContextSelector(ListingContext, (state) => state.listingDraftPayload);
  const setListingDraftAttribute = useContextSelector(ListingContext, (state) => state.setListingDraftAttribute);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isVariationsOpen, setIsVariationsOpen] = useState<boolean>(false);

  const imgSize = isWiderThanMd ? 120 : 80;

  const filteredModels = useMemo<ModelInterface[]>(() => {
    const suggestedIds = new Set(suggested?.map((s) => s.id));
    return models
      .filter((item: ModelInterface) => !suggestedIds.has(item.id))
      .filter((item: ModelInterface) => item.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm, models, suggested]);

  const filteredSuggested = useMemo<ModelInterface[]>(() => {
    if (!suggested?.length) {
      return [];
    }
    if (!searchTerm) {
      return suggested;
    }
    const lower = searchTerm.toLowerCase();
    return suggested.filter((item) => item.displayName.toLowerCase().includes(lower));
  }, [suggested, searchTerm]);

  /**
   *
   * @param e Change event of type HTMLInput Element
   * Matches model names to pull the selected models.
   */
  const handleSearchModel = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleModelClick = (model: ModelInterface) => {
    setListingDraftAttribute({
      macroModelId: model.id,
    });
    if (model.variations?.length > 0) {
      setIsVariationsOpen(true);
    } else {
      setIsModelsModalOpen(false);
    }
  };

  const handleModelNotFound = () => {
    setListingDraftAttribute({
      macroModelId: '',
      modelVariationId: '',
    });
    setIsModelsModalOpen(false);
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      <div className={styles.modelsModal}>
        <header className={styles.modelsModal__header}>
          <h2
            className={styles.modelsModal__title}
            id={modalDialogTitleId}
          >
            {t('PRICING_CALCULATOR.MODEL_SELECTION.TITLE')}
          </h2>
          <fieldset className={styles.modelsModal__search}>
            <label
              className="visuallyhidden"
              htmlFor="modelsModalInput"
            >
              {t('PRICING_CALCULATOR.MODEL_SELECTION.PLACEHOLDER')}
            </label>
            <Icon
              className={styles.modelsModal__search__icon}
              name="search-24"
              ariaHidden
            />
            <input
              ref={inputRef}
              id="modelsModalInput"
              type="text"
              placeholder={t('PRICING_CALCULATOR.MODEL_SELECTION.PLACEHOLDER')}
              onChange={handleSearchModel}
              value={searchTerm}
              className={styles.modelsModal__search__input}
            />
          </fieldset>
        </header>
        <main className={styles.modelsModal__main}>
          {filteredSuggested.length > 0 && (
            <div className={styles.modelsModal__suggested}>
              <h3 className={styles.modelsModal__suggestedTitle}>
                {t('PRICING_CALCULATOR.MODEL_SELECTION.SUGGESTED', { defaultValue: 'Suggested' })}
              </h3>
              <ul
                className={styles.modelsModal__list}
                role="listbox"
              >
                {filteredSuggested.map((model: ModelInterface) => (
                  <li
                    key={`suggested-${model.id}`}
                    role="option"
                    className={clsx(
                      styles.modelsModal__list__item,
                      styles['modelsModal__list__item--suggested'],
                      listingDraftPayload.macroModelId === model.id && styles.selected,
                    )}
                    onClick={() => handleModelClick(model)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleModelClick(model);
                      }
                    }}
                    aria-selected={listingDraftPayload.macroModelId === model.id}
                    tabIndex={0}
                  >
                    <VcImage
                      width={imgSize}
                      height={imgSize}
                      alt={model.displayName}
                      src={`/marque/modele/${model?.picture}`}
                      className={styles.modelsModal__list__img}
                    />
                    <span className={styles.modelsModal__list__text}>{model.displayName}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {filteredSuggested.length > 0 && filteredModels.length > 0 && (
            <h3 className={styles.modelsModal__otherTitle}>
              {t('PRICING_CALCULATOR.MODEL_SELECTION.OTHER_MODELS', { defaultValue: 'Other models' })}
            </h3>
          )}
          <ul
            className={styles.modelsModal__list}
            role="listbox"
            data-cy="models-list"
          >
            {filteredModels &&
              filteredModels.map((model: ModelInterface) => (
                <li
                  key={`model-${model.id}`}
                  role="option"
                  className={clsx(
                    styles.modelsModal__list__item,
                    listingDraftPayload.macroModelId === model.id && styles.selected,
                  )}
                  onClick={() => {
                    handleModelClick(model);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleModelClick(model);
                    }
                  }}
                  aria-selected={listingDraftPayload.macroModelId === model.id}
                  tabIndex={0}
                >
                  <VcImage
                    width={imgSize}
                    height={imgSize}
                    alt={model.displayName}
                    src={`/marque/modele/${model?.picture}`}
                    className={styles.modelsModal__list__img}
                  />
                  <span className={styles.modelsModal__list__text}>{model.displayName}</span>
                </li>
              ))}
          </ul>
        </main>
        <footer className={styles.modelsModal__footer}>
          <span>{t('PRICING_CALCULATOR.MODEL_SELECTION.OTHER_TEXT')}</span>
          <button
            type="button"
            className={clsx('vc-btn vc-btn--secondary vc-btn--small', styles.modelsModal__footer__cta)}
            onClick={handleModelNotFound}
          >
            {t('PRICING_CALCULATOR.MODEL_SELECTION.OTHER_CTA')}
          </button>
        </footer>
        <div className={clsx(styles.modelsModal__variations, isVariationsOpen && styles.open)}>
          <button
            type="button"
            onClick={() => {
              setIsVariationsOpen(false);
              setListingDraftAttribute({
                macroModelId: '',
                modelVariationId: '',
              });
            }}
            className={styles.modelsModal__variations__backCTA}
          >
            <Icon
              className={styles.modelsModal__variations__backCTA__icon}
              name="ios-arrow-left-20"
              ariaHidden
            />
            <span>{t('PRICING_CALCULATOR.MODEL_SELECTION.VARIATIONS.CTA_BACK')}</span>
          </button>
          <ModelVariations
            isDesktop={isWiderThanMd}
            setIsModelsModalOpen={setIsModelsModalOpen}
          />
        </div>
      </div>
    </>
  );
};
export default ModelsModal;
